import { isUnauthorizedError } from './unauthorizedError'

/**
 * True for a deterministic HTTP 403 "Access denied" from a project endpoint: a signed-in user the
 * API has permanently denied. Refetching the identical request returns the identical denial, so
 * surfaces use this to withhold the Retry control instead of offering one that cannot succeed.
 */
export function isProjectAccessDeniedError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  return 'status' in error && (error as { status?: unknown }).status === 403
}

/** True for HTTP 410: the project was soft-deleted. */
export function isProjectDeletedError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  return 'status' in error && (error as { status?: unknown }).status === 410
}

/**
 * Message for a failed `useGetProjectQuery`. Shared by both project surfaces (ProjectDetail and
 * ProjectOverview) because both gate their "Sign In" button on the 401 wording — a surface that
 * hardcodes a generic message instead leaves an unauthenticated visitor with no way to log in.
 *
 * `signedIn` is the auth state's own answer (the Redux user). A 401 that reaches a signed-in user
 * is a spurious rejection (bug-muh11lve-wv7a: the project fetch 401'd while /api/auth-me and
 * sibling requests confirmed the session), and telling that user to log in is false — it gets the
 * network-failure wording and a working Retry. Signed-out visitors keep the login wording that
 * gates the Sign in button.
 */
export function projectQueryErrorMessage(error: unknown, signedIn = false): string | null {
  if (!error) return null
  if (isProjectDeletedError(error)) return 'This project has been deleted'
  if (isProjectAccessDeniedError(error)) return 'You do not have access to this project'
  if (isUnauthorizedError(error)) {
    return signedIn ? 'Failed to fetch project' : 'Login required to view this project'
  }
  return 'Failed to fetch project'
}

/**
 * True when a failed query should be retried once: a 401 that contradicts the app's verified
 * signed-in state. The identical request re-fires with the same (valid) session cookie, which is
 * what recovered the sibling requests in the bug-muh11lve-wv7a recording. A signed-out visitor's
 * 401 must not double-fire — the sign-in redirect owns that case.
 */
export function shouldRetryUnauthorizedQuery(error: unknown, authUser: unknown): boolean {
  return authUser != null && isUnauthorizedError(error)
}
